import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/stores/authStore";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OrganizationManager } from "@/components/OrganizationManager";
import { EmailVerificationBanner } from "@/components/EmailVerification";
import { 
  Cloud, Plus, Trash2, ArrowLeft, Calendar, 
  Layers, ExternalLink, Loader2, FolderOpen, AlertTriangle, RefreshCw, Building2
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProjectsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("projects");

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/projects`, { headers, timeout: 30000 });
      setProjects(response.data);
    } catch (err) {
      let errorMsg = "Failed to load projects";
      if (err.code === 'ECONNABORTED') {
        errorMsg = "Request timed out. Please try again.";
      }
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/projects/${deleteId}`, { headers });
      setProjects(projects.filter(p => p.id !== deleteId));
      toast.success("Project deleted");
    } catch (error) {
      toast.error("Failed to delete project");
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const exportProject = async (projectId) => {
    try {
      const response = await axios.get(`${API}/export/${projectId}`);
      const blob = new Blob([response.data], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `architecture-${projectId}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Architecture exported!");
    } catch (error) {
      toast.error("Failed to export");
    }
  };

  const getPhaseLabel = (phase) => {
    const labels = {
      1: "Intake",
      2: "Follow-ups",
      3: "Architecture",
      4: "IaC Generated"
    };
    return labels[phase] || "Started";
  };

  const getPhaseColor = (phase) => {
    if (phase === 4) return "text-[#00ff9d] bg-[#00ff9d]/10 border-[#00ff9d]/20";
    if (phase === 3) return "text-[#00f0ff] bg-[#00f0ff]/10 border-[#00f0ff]/20";
    if (phase === 2) return "text-[#7000ff] bg-[#7000ff]/10 border-[#7000ff]/20";
    return "text-zinc-400 bg-zinc-800/50 border-zinc-700";
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#09090b]/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white"
                onClick={() => navigate('/')}
                data-testid="back-home-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Home
              </Button>
              <div className="w-px h-6 bg-white/10" />
              <div className="flex items-center gap-3 group">
                <div className="relative">
                  <div className="absolute inset-0 bg-[#00f0ff]/20 blur-xl rounded-full scale-75 group-hover:scale-100 transition-transform" />
                  <img src="/zerozone-logo.png" alt="ZeroZone Logo" className="relative w-28 h-28 object-contain drop-shadow-[0_0_18px_rgba(59,130,246,0.5)]" />
                </div>
                <span className="font-semibold text-white">Saved Projects</span>
              </div>
            </div>
            
            <Button
              className="bg-[#00f0ff] text-black font-semibold hover:bg-[#00f0ff]/90 rounded-sm"
              onClick={() => navigate('/wizard')}
              data-testid="new-project-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Email Verification Banner */}
        <EmailVerificationBanner className="mb-6" />

        {/* Tabs for Projects and Teams */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="bg-zinc-900/50 border border-white/5 p-1">
            <TabsTrigger 
              value="projects" 
              className="data-[state=active]:bg-[#00f0ff] data-[state=active]:text-black"
              data-testid="projects-tab"
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              My Projects
            </TabsTrigger>
            <TabsTrigger 
              value="teams" 
              className="data-[state=active]:bg-[#00f0ff] data-[state=active]:text-black"
              data-testid="teams-tab"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Teams
            </TabsTrigger>
          </TabsList>

          <TabsContent value="projects" className="mt-6">
            {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#00f0ff] animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-sm bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Failed to Load Projects</h2>
            <p className="text-zinc-500 mb-6">{error}</p>
            <Button
              className="bg-[#00f0ff] text-black font-semibold hover:bg-[#00f0ff]/90 rounded-sm"
              onClick={loadProjects}
              data-testid="retry-load-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-sm bg-zinc-900 border border-zinc-800 flex items-center justify-center mx-auto mb-6">
              <Cloud className="w-8 h-8 text-zinc-600" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">No projects yet</h2>
            <p className="text-zinc-500 mb-6">Create your first cloud architecture project</p>
            <Button
              className="bg-[#00f0ff] text-black font-semibold hover:bg-[#00f0ff]/90 rounded-sm"
              onClick={() => navigate('/wizard')}
              data-testid="create-first-project-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Project
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div
                key={project.id}
                className="bg-[#09090b] border border-white/5 rounded-sm p-6 hover:border-white/10 transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-[#00f0ff]/10 border border-[#00f0ff]/20 flex items-center justify-center">
                      <Cloud className="w-5 h-5 text-[#00f0ff]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white group-hover:text-[#00f0ff] transition-colors">
                        {project.name || "Untitled Project"}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded border ${getPhaseColor(project.current_phase)}`}>
                          {getPhaseLabel(project.current_phase)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-zinc-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteId(project.id);
                    }}
                    data-testid={`delete-project-${project.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-sm text-zinc-500">
                    <Layers className="w-4 h-4" />
                    <span>{project.phase1?.cloud_platform || "Platform not set"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-zinc-500">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(project.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    className="flex-1 bg-zinc-800 text-white hover:bg-zinc-700 rounded-sm"
                    onClick={() => navigate(`/wizard/${project.id}`)}
                    data-testid={`open-project-${project.id}`}
                  >
                    Continue
                  </Button>
                  {project.current_phase >= 3 && (
                    <Button
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 hover:bg-white/5 rounded-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        exportProject(project.id);
                      }}
                      data-testid={`export-project-${project.id}`}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
          </TabsContent>

          <TabsContent value="teams" className="mt-6">
            {isAuthenticated ? (
              <OrganizationManager />
            ) : (
              <div className="text-center py-16 px-4">
                <div className="w-16 h-16 rounded-sm bg-[#00f0ff]/10 border border-[#00f0ff]/20 flex items-center justify-center mx-auto mb-6">
                  <Building2 className="w-8 h-8 text-[#00f0ff]" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Teams & Organizations</h2>
                <p className="text-zinc-400 mb-6 max-w-md mx-auto">
                  Create organizations to collaborate with team members on architecture projects. 
                  Sign in to access this feature.
                </p>
                <Button
                  className="bg-[#00f0ff] text-black font-semibold hover:bg-[#00f0ff]/90 rounded-sm"
                  onClick={() => navigate('/?auth=login')}
                  data-testid="teams-signin-btn"
                >
                  Sign In to Continue
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#09090b] border border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Project?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This action cannot be undone. The project and all its data will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
